import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  PortfolioArtifact,
  PortfolioArtifactCreate,
  PortfolioArtifactUpdate,
  PortfolioArtifactArchetype,
  PortfolioArtifactStatus,
} from "../../api/client";
import { PROJECT_ARCHETYPES } from "../../data/portfolioKit";
import { L } from "../../utils/localized";

type Mode = "create" | "edit";

interface ArtifactFormDialogProps {
  open: boolean;
  mode: Mode;
  /** In edit mode, the artifact to prefill the form from. */
  initialArtifact?: PortfolioArtifact;
  /** Called when user clicks Cancel or backdrop. */
  onClose: () => void;
  /** Called when user submits a valid form. Parent handles the API call.
   *  Returns a Promise so the dialog can show a loading state. */
  onSubmit: (
    payload: PortfolioArtifactCreate | PortfolioArtifactUpdate,
  ) => Promise<void>;
}

const STATUS_OPTIONS: ReadonlyArray<{
  value: PortfolioArtifactStatus;
  labelKey: string;
}> = [
  { value: "in_progress", labelKey: "ArtifactFormDialog.statusOptionInProgress" },
  { value: "complete", labelKey: "ArtifactFormDialog.statusOptionComplete" },
  { value: "archived", labelKey: "ArtifactFormDialog.statusOptionArchived" },
];

const TITLE_MAX = 255;
const DESCRIPTION_MAX = 10_000;
const EXTERNAL_URL_MAX = 500;

/** Cast a free-form archetype string from the API back into the union type.
 *  The API returns `string` to allow future slugs; we check against the
 *  live PROJECT_ARCHETYPES list so new slugs are accepted automatically. */
function coerceArchetype(value: string | undefined): PortfolioArtifactArchetype {
  // slug is a locale-invariant routing identifier (never translated), so this
  // check is always done against the English wrapper regardless of locale.
  if (value && PROJECT_ARCHETYPES.some((a) => a.en.slug === value)) {
    return value as PortfolioArtifactArchetype;
  }
  return "rag-agent";
}

function coerceStatus(value: string | undefined): PortfolioArtifactStatus {
  if (value === "in_progress" || value === "complete" || value === "archived") {
    return value;
  }
  return "in_progress";
}

export default function ArtifactFormDialog({
  open,
  mode,
  initialArtifact,
  onClose,
  onSubmit,
}: ArtifactFormDialogProps) {
  const theme = useTheme();
  const { t, i18n } = useTranslation("portfolio-kit");
  const archetypeOptions = useMemo<
    ReadonlyArray<{ value: PortfolioArtifactArchetype; label: string }>
  >(
    () =>
      PROJECT_ARCHETYPES.map((a) => {
        const resolved = L(a, i18n.language);
        return { value: resolved.slug as PortfolioArtifactArchetype, label: resolved.title };
      }),
    [i18n.language],
  );
  const [title, setTitle] = useState("");
  const [archetype, setArchetype] =
    useState<PortfolioArtifactArchetype>("rag-agent");
  const [description, setDescription] = useState("");
  const [externalUrl, setExternalUrl] = useState("");
  const [status, setStatus] = useState<PortfolioArtifactStatus>("in_progress");

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [touchedSubmit, setTouchedSubmit] = useState(false);

  // Reset form state whenever the dialog opens. We key off `open` so closing
  // and re-opening always starts from a clean slate.
  useEffect(() => {
    if (!open) {
      return;
    }
    setSubmitError(null);
    setTouchedSubmit(false);
    setSubmitting(false);

    if (mode === "edit" && initialArtifact) {
      setTitle(initialArtifact.title);
      setArchetype(coerceArchetype(initialArtifact.archetype));
      setDescription(initialArtifact.description ?? "");
      setExternalUrl(initialArtifact.external_url ?? "");
      setStatus(coerceStatus(initialArtifact.status));
    } else {
      setTitle("");
      setArchetype("rag-agent");
      setDescription("");
      setExternalUrl("");
      setStatus("in_progress");
    }
  }, [open, mode, initialArtifact]);

  // --- Validation ---------------------------------------------------------
  const trimmedTitle = title.trim();
  const trimmedDescription = description.trim();
  const trimmedExternalUrl = externalUrl.trim();

  let titleError: string | null = null;
  if (trimmedTitle.length === 0) {
    titleError = t("ArtifactFormDialog.titleRequired");
  } else if (title.length > TITLE_MAX) {
    titleError = t("ArtifactFormDialog.titleTooLong", { max: TITLE_MAX });
  }

  const descriptionError =
    description.length > DESCRIPTION_MAX
      ? t("ArtifactFormDialog.descriptionTooLong", { max: DESCRIPTION_MAX })
      : null;

  let externalUrlError: string | null = null;
  if (trimmedExternalUrl.length > 0) {
    if (externalUrl.length > EXTERNAL_URL_MAX) {
      externalUrlError = t("ArtifactFormDialog.urlTooLong", {
        max: EXTERNAL_URL_MAX,
      });
    } else if (
      !trimmedExternalUrl.startsWith("http://") &&
      !trimmedExternalUrl.startsWith("https://")
    ) {
      externalUrlError = t("ArtifactFormDialog.urlMustStartWithHttp");
    }
  }

  const hasValidationError = Boolean(
    titleError || descriptionError || externalUrlError,
  );

  // --- Edit-mode change detection -----------------------------------------
  // Compute the diff between the form and `initialArtifact`. In edit mode the
  // payload only contains fields that actually changed (true PATCH).
  function buildUpdatePayload(): PortfolioArtifactUpdate {
    const payload: PortfolioArtifactUpdate = {};
    if (!initialArtifact) {
      return payload;
    }

    if (trimmedTitle !== initialArtifact.title) {
      payload.title = trimmedTitle;
    }
    if (archetype !== coerceArchetype(initialArtifact.archetype)) {
      payload.archetype = archetype;
    }
    const originalDescription = initialArtifact.description ?? "";
    if (trimmedDescription !== originalDescription) {
      payload.description = trimmedDescription;
    }
    const originalUrl = initialArtifact.external_url ?? "";
    if (trimmedExternalUrl !== originalUrl) {
      payload.external_url = trimmedExternalUrl;
    }
    if (status !== coerceStatus(initialArtifact.status)) {
      payload.status = status;
    }

    return payload;
  }

  const editPayload = mode === "edit" ? buildUpdatePayload() : null;
  const noChanges =
    mode === "edit" && editPayload !== null && Object.keys(editPayload).length === 0;

  const saveDisabled =
    submitting ||
    trimmedTitle.length === 0 ||
    hasValidationError ||
    (mode === "edit" && noChanges);

  // --- Submit -------------------------------------------------------------
  async function handleSubmit() {
    setTouchedSubmit(true);
    if (hasValidationError || trimmedTitle.length === 0) {
      return;
    }

    let payload: PortfolioArtifactCreate | PortfolioArtifactUpdate;
    if (mode === "create") {
      const createPayload: PortfolioArtifactCreate = {
        title: trimmedTitle,
        archetype,
        description: trimmedDescription.length > 0 ? trimmedDescription : undefined,
        external_url:
          trimmedExternalUrl.length > 0 ? trimmedExternalUrl : undefined,
      };
      payload = createPayload;
    } else {
      payload = buildUpdatePayload();
      if (Object.keys(payload).length === 0) {
        // Nothing to send. Guard rail; button should already be disabled.
        return;
      }
    }

    setSubmitting(true);
    setSubmitError(null);
    try {
      await onSubmit(payload);
      onClose();
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : t("ArtifactFormDialog.saveFailedFallback");
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  }

  function handleClose() {
    if (submitting) {
      return;
    }
    onClose();
  }

  // Only surface validation errors after the user tries to submit, so the
  // form doesn't yell red on first open.
  const showTitleError = touchedSubmit && Boolean(titleError);
  const showDescriptionError = touchedSubmit && Boolean(descriptionError);
  const showUrlError = Boolean(externalUrlError); // URL format is obvious enough to show eagerly

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {mode === "create"
          ? t("ArtifactFormDialog.createTitle")
          : t("ArtifactFormDialog.editTitle")}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          {submitError && (
            <Alert severity="error" onClose={() => setSubmitError(null)}>
              {submitError}
            </Alert>
          )}

          <TextField
            label={t("ArtifactFormDialog.titleFieldLabel")}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            required
            autoFocus={mode === "create"}
            error={showTitleError}
            helperText={showTitleError ? titleError : " "}
            disabled={submitting}
          />

          <FormControl fullWidth disabled={submitting}>
            <InputLabel id="artifact-archetype-label">
              {t("ArtifactFormDialog.archetypeFieldLabel")}
            </InputLabel>
            <Select
              labelId="artifact-archetype-label"
              label={t("ArtifactFormDialog.archetypeFieldLabel")}
              value={archetype}
              onChange={(e) =>
                setArchetype(e.target.value as PortfolioArtifactArchetype)
              }
            >
              {archetypeOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {t("ArtifactFormDialog.archetypeHelperText")}
            </FormHelperText>
          </FormControl>

          <TextField
            label={t("ArtifactFormDialog.descriptionFieldLabel")}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={4}
            error={showDescriptionError}
            helperText={
              showDescriptionError
                ? descriptionError
                : t("ArtifactFormDialog.descriptionHelperText")
            }
            disabled={submitting}
          />

          <TextField
            label={t("ArtifactFormDialog.externalUrlFieldLabel")}
            value={externalUrl}
            onChange={(e) => setExternalUrl(e.target.value)}
            fullWidth
            placeholder={t("ArtifactFormDialog.externalUrlPlaceholder")}
            error={showUrlError}
            helperText={showUrlError ? externalUrlError : " "}
            disabled={submitting}
          />

          {mode === "edit" && (
            <FormControl fullWidth disabled={submitting}>
              <InputLabel id="artifact-status-label">
                {t("ArtifactFormDialog.statusFieldLabel")}
              </InputLabel>
              <Select
                labelId="artifact-status-label"
                label={t("ArtifactFormDialog.statusFieldLabel")}
                value={status}
                onChange={(e) =>
                  setStatus(e.target.value as PortfolioArtifactStatus)
                }
              >
                {STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {t(opt.labelKey)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {mode === "edit" && noChanges && (
            <FormHelperText sx={{ mt: -1 }}>
              {t("ArtifactFormDialog.noChanges")}
            </FormHelperText>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={submitting} variant="text">
          {t("ArtifactFormDialog.cancelButton")}
        </Button>
        <Box sx={{ position: "relative" }}>
          <Button
            onClick={handleSubmit}
            disabled={saveDisabled}
            variant="contained"
            sx={{
              backgroundColor: theme.palette.primary.main,
              "&:hover": { backgroundColor: theme.palette.primary.dark },
            }}
          >
            {t("ArtifactFormDialog.saveButton")}
          </Button>
          {submitting && (
            <CircularProgress
              size={20}
              sx={{
                color: theme.palette.primary.main,
                position: "absolute",
                top: "50%",
                left: "50%",
                marginTop: "-10px",
                marginLeft: "-10px",
              }}
            />
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
}
